import { useEffect, useState } from "react";
import { Flame, Check, X, Loader2, GraduationCap } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";
import { coinRain } from "./coinRain";

const MELA_TOAST = { background: "#1A1A1A", color: "#F0C742", border: "1px solid rgba(226,88,34,0.5)" };

const UstaadRound = ({ trivia, refresh }) => {
  const [round, setRound] = useState(null);
  const [choices, setChoices] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/ustaad/start", { user_id: USER_ID });
      setRound(r.data);
      setChoices(new Array(r.data.questions.length).fill(null));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/ustaad/submit", { user_id: USER_ID, session_id: round.session_id, choices });
      setResult(r.data);
      if (r.data.perfect) { toast.success("PERFECT USTAAD ROUND! ਕਮਾਲ!", { style: MELA_TOAST }); coinRain(); }
      if (r.data.mela_result?.awarded) melaToast(r.data.mela_result.message);
      badgeToast(r.data.new_badges);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  if (!trivia.ustaad_open && !result) {
    return <p data-testid="ustaad-closed" className="text-[11px] text-white/45 mt-2">This week's Ustaad round is done — a fresh one opens Monday.</p>;
  }

  if (!round) {
    return (
      <button data-testid="ustaad-start" onClick={start} disabled={busy}
        className="mt-2 flex items-center gap-2 h-10 px-5 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-black tracking-widest disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <GraduationCap size={13} />}
        TAKE THE WEEKLY ROUND — 3 HARD QUESTIONS
      </button>
    );
  }

  if (result) {
    return (
      <div data-testid="ustaad-result" className="mt-3 rounded-xl border border-[#D4AF37]/40 bg-[#1C160A] p-4">
        <p className={`font-display text-xl font-bold ${result.perfect ? "text-[#D4AF37]" : ""}`}>
          {result.correct}/{result.total} {result.perfect ? "— FLAVOR USTAAD! " : ""}
        </p>
        {result.perfect && <p className="text-xs text-[#F0C742] font-bold mt-1">PERFECT ROUND — ਕਮਾਲ! Pure Ustaad bragging rights.</p>}
        {!result.perfect && result.mela_result && <p className="text-xs text-[#F0C742] font-bold mt-1 flex items-center justify-center gap-1.5"><MelaCoin size={15} /> {result.mela_result.message}</p>}
        <div className="mt-2 space-y-2">
          {result.results.map((r, i) => (
            <p key={i} className="text-[11px] text-white/60 leading-snug flex gap-1.5">
              {r.correct ? <Check size={12} className="text-green-400 shrink-0 mt-0.5" /> : <X size={12} className="text-[#E25822] shrink-0 mt-0.5" />}
              {r.fact}
            </p>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ustaad-round" className="mt-3 rounded-xl border border-[#D4AF37]/40 bg-[#17110A] p-4 space-y-4">
      {round.questions.map((q, qi) => (
        <div key={qi}>
          <p className="text-sm font-bold leading-snug">{qi + 1}. {q.q}</p>
          <div className="grid gap-1.5 mt-2">
            {q.options.map((o, oi) => (
              <button key={oi} data-testid={`ustaad-q${qi}-opt${oi}`}
                onClick={() => setChoices((c) => c.map((v, i) => (i === qi ? oi : v)))}
                className={`text-left rounded-lg border px-3 py-2 text-xs font-semibold ${choices[qi] === oi ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 bg-[#1A1A1A]"}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button data-testid="ustaad-submit" onClick={submit} disabled={busy || choices.some((c) => c === null)}
        className="w-full h-11 rounded-full bg-[#D4AF37] text-black font-black text-xs tracking-widest disabled:opacity-40">
        {busy ? <Loader2 size={13} className="animate-spin mx-auto" /> : "SUBMIT ROUND"}
      </button>
    </div>
  );
};

export const FlavorTrivia = ({ state, refresh }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const trivia = state?.trivia || {};
  const mc = trivia.mela_correct ?? 5;
  const mw = trivia.mela_wrong ?? 5;

  useEffect(() => {
    api.get("/arcade/trivia", { params: { user_id: USER_ID } }).then((r) => {
      setData(r.data);
      if (r.data.answered && r.data.result) {
        setOutcome(r.data.result);
        setPicked(r.data.result.choice);
      }
    }).catch(() => {});
  }, []);

  const answer = async (i) => {
    if (busy || outcome) return;
    setBusy(true);
    setPicked(i);
    try {
      const r = await api.post("/arcade/trivia/answer", { user_id: USER_ID, choice: i });
      setOutcome(r.data);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
      setPicked(null);
    }
    setBusy(false);
  };

  if (!data) return <div className="rounded-2xl bg-[#121212] border border-white/10 p-5"><Loader2 size={16} className="animate-spin text-[#D4AF37]" /></div>;
  const streak = outcome?.streak ?? data.streak;

  return (
    <div data-testid="game-flavor-trivia" className="rounded-2xl bg-[#121212] border border-[#E25822]/30 p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] tracking-[0.3em] text-[#E25822] font-bold">PUNJAB FLAVOR PEHCHAAN · ਸੁਆਦ ਪਛਾਣ</p>
          <p className="text-xs text-white/50 mt-1">Daily question: +{mc} Regal Coins right, −{mw} wrong · streak bonus every 7 days.</p>
        </div>
        <span data-testid="trivia-streak" className="flex items-center gap-1 rounded-full border border-[#E25822]/50 bg-[#E25822]/10 px-3 py-1.5 text-xs font-black text-[#E25822]">
          <Flame size={13} fill={streak > 0 ? "currentColor" : "none"} /> {streak} DAY{streak === 1 ? "" : "S"}
        </span>
      </div>
      <p data-testid="trivia-question" className="font-display text-xl font-bold leading-snug mt-4">{data.question}</p>
      <div className="grid gap-2 mt-3">
        {data.options.map((o, i) => {
          const isAnswer = outcome && i === outcome.answer;
          const isPicked = i === picked;
          return (
            <button key={i} data-testid={`trivia-option-${i}`} onClick={() => answer(i)} disabled={busy || !!outcome}
              className={`text-left rounded-xl border px-4 py-3 text-sm font-semibold transition-colors flex items-center justify-between gap-2 ${
                isAnswer ? "border-green-500 bg-green-500/10 text-green-300"
                  : isPicked && outcome ? "border-[#E25822] bg-[#E25822]/10 text-[#E25822]"
                    : isPicked ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-white/10 bg-[#1A1A1A] hover:border-[#D4AF37]/40"}`}>
              {o}
              {isAnswer && <Check size={15} />}
              {isPicked && outcome && !isAnswer && <X size={15} />}
            </button>
          );
        })}
      </div>
      {outcome && (
        <div data-testid="trivia-outcome" className="mt-4 rounded-xl border border-white/10 bg-[#1A1A1A] p-4">
          <p className={`text-[10px] tracking-[0.3em] font-bold flex items-center gap-1.5 ${outcome.correct ? "text-green-400" : "text-[#E25822]"}`}>
            <MelaCoin size={14} /> {outcome.correct ? `SHABASH! +${mc} REGAL COINS` : `NOT QUITE — −${mw} REGAL COINS, STREAK RESETS`}
          </p>
          <p data-testid="trivia-fact" className="text-sm text-white/70 leading-relaxed mt-2">{outcome.fact}</p>
          {outcome.streak_bonus?.awarded && (
            <p className="text-xs text-[#F0C742] font-bold mt-2">7-DAY STREAK — {outcome.streak_bonus.message}!</p>
          )}
          <p className="text-[11px] text-white/40 mt-2">A fresh question lands tomorrow.</p>
        </div>
      )}

      <div className="mt-5 border-t border-white/10 pt-4">
        <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1.5">
          <GraduationCap size={12} /> FLAVOR USTAAD — WEEKLY DIFFICULT ROUND
        </p>
        <p className="text-[11px] text-white/50 mt-1">
          3 hard questions, once a week. Each correct answer pays {trivia.ustaad_mela_per_correct ?? 20} Regal Coins — a perfect round earns the Flavor Ustaad badge.
        </p>
        <UstaadRound trivia={trivia} refresh={refresh} />
      </div>
    </div>
  );
};
